import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { enviosCampanaService, type EnvioCampanaConDetalles } from '../services/enviosCampanaService';
import type { OwnerFondoCampana } from '../types/fondosCampana';
import { puedeOperarDinero } from '../utils/permissions';
import { RemitirCampanaModal } from './RemitirCampanaModal';
import { EnvioCampanaFila } from './enviosCampana/EnvioCampanaFila';
import { entidadDineroDelOwner } from './enviosCampana/accionesEnvio';
import { Button } from './ui/Button';
import { FormErrorAlert } from './ui/FormErrorAlert';
interface Props {
  readonly owner: OwnerFondoCampana; readonly readOnly?: boolean;
  readonly campanaId?: string; readonly campanaMasterId?: string; readonly onRefresh?: () => void;
}
export function EnviosCampanaPanel({ owner, readOnly = false, campanaId, campanaMasterId, onRefresh }: Props) {
  const { usuario } = useAuth();
  const [envios, setEnvios] = useState<EnvioCampanaConDetalles[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [remitirMasterId, setRemitirMasterId] = useState<string | null>(null);
  const puedeOperar = !readOnly && puedeOperarDinero(usuario, entidadDineroDelOwner(owner));
  const tipo = owner.tipo, id = owner.id, nombre = owner.nombre;
  const consultar = useCallback(async () => {
    const entidad: OwnerFondoCampana = { tipo, id, nombre };
    if (tipo === 'lumisial') {
      if (campanaId) return enviosCampanaService.getByCampana(campanaId);
      return enviosCampanaService.getPorOrigen(entidad, { campanaMasterId });
    }
    return (await Promise.all([enviosCampanaService.getTodosParaAdmin(entidad, { campanaMasterId }),
      enviosCampanaService.getPorOrigen(entidad, { campanaMasterId })])).flat();
  }, [tipo, id, nombre, campanaId, campanaMasterId]);
  const recibir = useCallback((datos: EnvioCampanaConDetalles[]) => {
    setEnvios([...new Map(datos.map(e => [e.id, e])).values()]); setError(null); setCargando(false);
  }, []);
  const fallo = useCallback(() => {
    setError('No pudimos consultar los envíos. Puedes reintentar sin repetir ninguna transferencia.'); setCargando(false);
  }, []);
  useEffect(() => {
    let vigente = true;
    consultar().then(datos => { if (vigente) recibir(datos); }).catch(() => { if (vigente) fallo(); });
    return () => { vigente = false; };
  }, [consultar, recibir, fallo]);
  const recargar = () => { setCargando(true); void consultar().then(recibir).catch(fallo); };
  const actualizar = () => { recargar(); onRefresh?.(); };
  return <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div><h3 className="font-semibold text-slate-800">Envíos y rendiciones de campañas</h3>
        <p className="text-sm text-slate-600">Entradas y salidas de {nombre}. Cada junta administra su propia entidad.</p></div>
    </div>
    {cargando && <output className="block">Consultando envíos…</output>}
    <FormErrorAlert error={error} />
    {error && <Button variant="secondary" onClick={recargar}>Reintentar consulta</Button>}
    {!cargando && !error && envios.length === 0 && <p className="text-sm text-slate-500">No hay envíos registrados para esta entidad.</p>}
    {!cargando && !error && envios.map(envio => <EnvioCampanaFila key={envio.id} envio={envio} owner={owner}
      puedeOperar={puedeOperar} onRefresh={actualizar} onRemitir={setRemitirMasterId} />)}
    {remitirMasterId && tipo === 'diocesis' && <RemitirCampanaModal campanaMasterId={remitirMasterId}
      diocesisId={id} readOnly={!puedeOperar} onClose={() => setRemitirMasterId(null)} onSuccess={actualizar} />}
  </section>;
}
